import { useMemo } from 'react';
import { Dialog, DialogContent, DialogFooter } from './ui/dialog';
import { Button } from './ui';
import { Badge } from './ui/badge';
import { useAuth } from '@/hooks';
import { useLoginModalStore } from '@/stores/login-modal-store';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { campaignRepository } from '@/repositories/campaign-repository';
import { CAMPAIGN_CFG } from '@/configs/campaign-config';
import { useSuiClient } from '@mysten/dapp-kit';
import { Loader2, CheckCircle2, Circle, Share2 } from 'lucide-react';
import { cn } from '@/utils';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import suifrenPlayAnimation from '@/assets/welcome-modal/suifren-play.lottie';
import firstTicketImage from '@/assets/welcome-modal/first-ticket.svg';
import suigarSlushHeader from '@/assets/welcome-modal/suigar-x-slush-header.svg';
import { nftRepository } from '@/repositories/nft-repository';
import { QueryKeys } from '@/consts';
import { Progress } from '@/components/ui/progress';
import { GAMES } from '@/constants/games';
import fiveWinnersImage from '@/assets/welcome-modal/five-winners-shadows.svg';
import { useCountdown } from '@/hooks/use-countdown';
import { formatDateRange, INTEGER_FORMATTER, NUMBER_FORMATTER } from '@/utils/format-date-range';

interface WelcomeModalProps {
	isOpen: boolean;
	onClose: () => void;
}

const { ticketing, timeframe, name: campaignName } = CAMPAIGN_CFG;

const timeframeLabel = formatDateRange(timeframe.start, timeframe.end);

const DEFAULT_NFT_NAME = 'Campaign NFT';

type CampaignBonusNftDetails = {
	key: string;
	owned: boolean;
	name: string;
	imageUrl: string | null;
	isLoading: boolean;
	specId?: string;
};

const WelcomeModal: React.FC<WelcomeModalProps> = ({ isOpen, onClose }) => {
	const { isLogged, currentAccount, address: primaryAddress } = useAuth();
	const address = primaryAddress ?? currentAccount?.address ?? null;
	const { openLoginModal } = useLoginModalStore();
	const navigate = useNavigate();
	const client = useSuiClient();
	const bonusNfts = ticketing.bonusNfts ?? [];
	const bonusCombinationStrategy = ticketing.bonusCombinationStrategy ?? 'max';
	const campaignCountdown = useCountdown(timeframe.end, 1000);
	const campaignTimeLeftLabel = useMemo(() => {
		if (campaignCountdown.isExpired) return 'Results soon';
		if (campaignCountdown.days > 0) return `${campaignCountdown.days}d ${campaignCountdown.hours}h`;
		if (campaignCountdown.hours > 0) return `${campaignCountdown.hours}h ${campaignCountdown.minutes}m`;
		if (campaignCountdown.minutes > 0) return `${campaignCountdown.minutes}m ${campaignCountdown.seconds}s`;
		return `${campaignCountdown.seconds}s`;
	}, [campaignCountdown.days, campaignCountdown.hours, campaignCountdown.isExpired, campaignCountdown.minutes, campaignCountdown.seconds]);
	const campaignTimeLeftSubtext = campaignCountdown.isExpired ? 'Results are being finalized' : 'until results lock in';

	const handleClose = () => {
		onClose();
	};

	const handleActionClick = () => {
		if (isLogged) {
			const availableGames = GAMES.filter((game) => game.isGame);
			const selectedGame = availableGames[Math.floor(Math.random() * availableGames.length)];
			navigate({ to: selectedGame?.link || '/coinflip' });
		} else {
			openLoginModal();
		}
		onClose();
	};

	const handleNftCardClick = (target?: string | null) => {
		navigate({
			to: '/nft',
			search: (prev?: Record<string, unknown>) => ({
				...(prev ?? {}),
				focus: target ?? undefined,
			}),
		});
		onClose();
	};

	const canFetchCampaignTickets = Boolean(CAMPAIGN_CFG.features.campaignWalletEnabled !== false && isLogged && address);

	const ticketsQuery = useQuery({
		queryKey: ['campaign', CAMPAIGN_CFG.id, 'tickets', address],
		queryFn: () =>
			campaignRepository.getTicketSummary({
				address: address!,
				start: timeframe.start,
				end: timeframe.end,
			}),
		enabled: canFetchCampaignTickets,
		staleTime: 20_000,
		refetchInterval: 20_000,
	});

	const nftFactoryQuery = useQuery({
		queryKey: [QueryKeys.NftFactory],
		queryFn: () => nftRepository.getNftFactory(client),
		enabled: Boolean(isOpen),
		staleTime: 10 * 60_000,
	});

	const ownedNftsQuery = useQuery({
		queryKey: [QueryKeys.OwnedNft, address],
		queryFn: () => nftRepository.getOwnedNft(client, address!),
		enabled: Boolean(isOpen && isLogged && address),
		staleTime: 2 * 60_000,
	});

	const normalizedOwnedNames = useMemo(() => {
		if (!ownedNftsQuery.data) return [];
		return ownedNftsQuery.data.map((nft) => nft.name.toLowerCase());
	}, [ownedNftsQuery.data]);

	const bonusDetails: CampaignBonusNftDetails[] = useMemo(() => {
		if (bonusNfts.length === 0) return [];
		const specs = nftFactoryQuery.data?.specs ?? [];
		return bonusNfts.map((bonus, index) => {
			const matcher = bonus.nameIncludes?.toLowerCase().trim() ?? '';
			const matchedSpec = matcher ? specs.find((spec) => spec.name.toLowerCase().includes(matcher)) : undefined;
			const key = matcher || `bonus-${index}`;
			const owned = matcher ? normalizedOwnedNames.some((name) => name.includes(matcher)) : false;

			return {
				key,
				owned,
				name: matchedSpec?.name ?? DEFAULT_NFT_NAME,
				imageUrl: matchedSpec?.url ?? null,
				specId: matchedSpec?.id,
				isLoading: nftFactoryQuery.isLoading || (isLogged && ownedNftsQuery.isLoading),
			};
		});
	}, [bonusNfts, nftFactoryQuery.data, nftFactoryQuery.isLoading, normalizedOwnedNames, ownedNftsQuery.isLoading, isLogged]);

	const totalWagered = ticketsQuery.data?.totalWagered ?? 0;
	const baseTickets = useMemo(() => {
		if (totalWagered < ticketing.firstTicketThreshold) return 0;
		if (ticketing.additionalTicketStep <= 0) return 1;
		return 1 + Math.floor((totalWagered - ticketing.firstTicketThreshold) / ticketing.additionalTicketStep);
	}, [totalWagered]);

	const ownedBonusStatuses = bonusDetails.map((detail) => detail.owned);
	const isAnyBonusLoading = bonusDetails.some((detail) => detail.isLoading);
	const ownedBonusEntries = bonusNfts.reduce<{ index: number; multiplier: number }[]>((acc, bonus, index) => {
		if (ownedBonusStatuses[index]) {
			acc.push({ index, multiplier: bonus.multiplier });
		}
		return acc;
	}, []);
	const ownedBonusMultipliers = ownedBonusEntries.map((entry) => entry.multiplier);
	const combinedBonusMultiplier =
		ownedBonusMultipliers.length === 0
			? 1
			: bonusCombinationStrategy === 'multiply'
				? ownedBonusMultipliers.reduce((acc, value) => acc * value, 1)
				: Math.max(...ownedBonusMultipliers);
	const highestOwnedMultiplier = bonusCombinationStrategy === 'max' && ownedBonusEntries.length > 0 ? Math.max(...ownedBonusMultipliers) : null;
	const finalMultiplier = ticketing.baseMultiplier * combinedBonusMultiplier;
	const finalTickets = Math.round(baseTickets * finalMultiplier);
	const additionalTickets = Math.max(baseTickets - 1, 0);
	const remainingToFirst = Math.max(ticketing.firstTicketThreshold - totalWagered, 0);
	const progressToFirst = ticketing.firstTicketThreshold > 0 ? Math.min(totalWagered / ticketing.firstTicketThreshold, 1) : 0;
	const formattedAdditionalStep = NUMBER_FORMATTER.format(ticketing.additionalTicketStep);
	const ticketStackLabel = INTEGER_FORMATTER.format(baseTickets);
	const ticketPluralLabel = baseTickets === 1 ? 'ticket' : 'tickets';
	const frenzyBadgeLabel = baseTickets > 0 ? `Streak ×${ticketStackLabel}` : 'Streak idle';
	const canChaseStreak = ticketing.additionalTicketStep > 0;
	const overFirstTicket = Math.max(totalWagered - ticketing.firstTicketThreshold, 0);
	const showNextTicketMeter = Boolean(isLogged && baseTickets > 0 && canChaseStreak);
	const extraRemainder = showNextTicketMeter ? overFirstTicket % ticketing.additionalTicketStep : 0;
	const nextTicketCountdown = showNextTicketMeter
		? extraRemainder === 0
			? ticketing.additionalTicketStep
			: ticketing.additionalTicketStep - extraRemainder
		: remainingToFirst;
	const nextTicketProgress = showNextTicketMeter
		? ((ticketing.additionalTicketStep - nextTicketCountdown) / ticketing.additionalTicketStep) * 100
		: 0;
	const formattedNextTicketCountdown = NUMBER_FORMATTER.format(nextTicketCountdown);
	const frenzyDescription = !isLogged
		? 'Connect your wallet and let the ticket counter snowball on every bet.'
		: baseTickets > 0
			? `Collect as much ticket as you can to improve your chance to win! Keep feeding +${formattedAdditionalStep} ${ticketing.wageringCoinSymbol} bursts to stay on the streak.`
			: `Clear Step 1 to unlock more tickets. After that, every +${formattedAdditionalStep} ${ticketing.wageringCoinSymbol} mints another ticket.`;
	const frenzySubtext = !isLogged
		? 'Sign in and every wager automatically feeds the streak.'
		: baseTickets > 0
			? additionalTickets > 0
				? `Already printed ${INTEGER_FORMATTER.format(additionalTickets)} bonus ticket${additionalTickets === 1 ? '' : 's'}. Keep going!`
				: `Each +${formattedAdditionalStep} ${ticketing.wageringCoinSymbol} from here spins up another ticket.`
			: `Push past Step 1 and the meter starts printing a ticket every +${formattedAdditionalStep} ${ticketing.wageringCoinSymbol}.`;

	const ticketsLoading = ticketsQuery.isLoading;
	const ownedBonusCount = ownedBonusStatuses.filter(Boolean).length;
	const hasActiveBonus = ownedBonusCount > 0;
	const isBonusActive = (index: number) => {
		if (!ownedBonusStatuses[index]) return false;
		if (bonusCombinationStrategy === 'max') {
			if (highestOwnedMultiplier === null) return false;
			return bonusNfts[index].multiplier === highestOwnedMultiplier;
		}
		return true;
	};

	const steps = [
		{
			key: 'wager',
			title: `Wager ${ticketing.firstTicketThreshold} ${ticketing.wageringCoinSymbol}`,
			completed: baseTickets > 0,
			progress: progressToFirst,
			description: isLogged
				? baseTickets > 0
					? `You have wagered ${NUMBER_FORMATTER.format(Math.min(totalWagered, ticketing.firstTicketThreshold))} ${ticketing.wageringCoinSymbol}!`
					: `Play ${NUMBER_FORMATTER.format(remainingToFirst)} ${ticketing.wageringCoinSymbol} to unlock your first ticket.`
				: 'Connect your wallet to start tracking your wagers for this campaign.',
			value: isLogged ? `${NUMBER_FORMATTER.format(totalWagered)} ${ticketing.wageringCoinSymbol}` : null,
		},
		{
			key: 'extra',
			title: `Add +1 ticket every +${formattedAdditionalStep} ${ticketing.wageringCoinSymbol}`,
			completed: baseTickets > 0,
			description: frenzyDescription,
			badge: frenzyBadgeLabel,
		},
		{
			key: 'nft',
			title: 'Activate NFT multipliers',
			completed: bonusNfts.length > 0 && ownedBonusCount === bonusNfts.length,
			description:
				bonusNfts.length === 0
					? 'Campaign NFTs grant additional multipliers as they drop during the event.'
					: `Active NFTs: ${ownedBonusCount}/${bonusNfts.length}. ` +
						(bonusCombinationStrategy === 'max'
							? 'Only the highest NFT multiplier you own is applied.'
							: 'Collect them all to maximize your ticket multipliers!'),
			badge: <>{hasActiveBonus ? 'Multipliers active' : 'No multiplier'}</>,
			extra:
				bonusNfts.length === 0 ? null : (
					<>
						<div className="grid gap-3 pt-2 sm:grid-cols-2">
							{bonusNfts.map((bonus, index) => {
								const detail = bonusDetails[index];
								const owned = ownedBonusStatuses[index];
								const active = isBonusActive(index);
								const imageUrl = detail?.imageUrl ?? null;
								const hasImage = Boolean(imageUrl);
								const showSpinner = detail?.isLoading ?? false;
								const displayName = detail?.name ?? DEFAULT_NFT_NAME;
								const cardKey = detail?.key ?? `bonus-${index}`;
								const focusTarget = detail?.specId ?? detail?.key ?? displayName;

								return (
									<div
										key={cardKey}
										role="button"
										tabIndex={0}
										className={cn(
											'flex  flex-col items-center gap-2 rounded-2xl border p-3 text-left shadow-sm transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
											active ? 'border-success/60 bg-success/10' : owned ? 'border-primary/40 bg-primary/5' : 'border-border/60 bg-card/60',
										)}
										onClick={() => handleNftCardClick(focusTarget)}
										onKeyDown={(event) => {
											if (event.key === 'Enter' || event.key === ' ') {
												event.preventDefault();
												handleNftCardClick(focusTarget);
											}
										}}
									>
										<div className="flex items-center justify-between gap-2">
											<span className="text-sm font-semibold text-foreground">{displayName}</span>
											<Badge variant={owned ? 'secondary' : 'outline'} size="sm" className="uppercase tracking-wide">
												{owned ? (active ? 'Active' : 'Owned') : 'Mint me'}
											</Badge>
										</div>
										<div className="relative flex size-32 items-center justify-center rounded-xl bg-background/70">
											{showSpinner && (
												<div className="absolute inset-0 flex items-center justify-center bg-background/60">
													<Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
												</div>
											)}
											{hasImage ? (
												<img src={imageUrl ?? undefined} alt={displayName} className="h-full w-full rounded-xl object-cover" loading="lazy" />
											) : (
												<span className="px-4 text-xs font-medium text-muted-foreground">Preview unavailable</span>
											)}
											<Badge
												variant={active ? 'success' : 'primary'}
												className="absolute -right-2 -top-1 rounded-full px-2 py-0 font-semibold uppercase tracking-wide"
											>
												<span className="font-shadow-stroke-2xl font-serif -mt-0.5 text-[14px]">×{bonus.multiplier}</span>
											</Badge>
										</div>
										{!owned ? (
											<Button
												variant="primary"
												size="sm"
												className="py-0.5 text-xs font-semibold"
												onClick={(event) => {
													event.stopPropagation();
													handleNftCardClick(focusTarget);
												}}
											>
												Mint this NFT
											</Button>
										) : null}
									</div>
								);
							})}
						</div>
						{bonusCombinationStrategy === 'max' ? (
							<p className="pt-2 text-center text-[11px] font-medium text-muted-foreground/80">
								NFT multipliers are not cumulative, only the highest multiplier you own is applied.
							</p>
						) : null}
					</>
				),
		},
	];

	const multiplierBreakdownParts: number[] = [];
	if (ticketing.baseMultiplier !== 1) {
		multiplierBreakdownParts.push(ticketing.baseMultiplier);
	}
	if (bonusCombinationStrategy === 'multiply') {
		ownedBonusMultipliers.forEach((value) => {
			if (value !== 1) {
				multiplierBreakdownParts.push(value);
			}
		});
	} else if (highestOwnedMultiplier && highestOwnedMultiplier !== 1) {
		multiplierBreakdownParts.push(highestOwnedMultiplier);
	}
	const multiplierExpression =
		multiplierBreakdownParts.length > 0 ? multiplierBreakdownParts.map((value) => `× ${NUMBER_FORMATTER.format(value)}`).join(' ') : '× 1';
	const formattedMultiplierExpression = multiplierExpression.trim();
	const formulaText =
		baseTickets > 0
			? `${INTEGER_FORMATTER.format(baseTickets)} ${formattedMultiplierExpression} = ${INTEGER_FORMATTER.format(finalTickets)}`
			: 'No tickets yet, start wagering to unlock your first entry.';
	const handleShareOnX = () => {
		if (typeof window === 'undefined') return;
		const ticketsText = INTEGER_FORMATTER.format(finalTickets);
		const text = `I just stacked ${ticketsText} tickets in the @Suigar_com x @SlushWallet $5,000 giveaway! `;
		const shareTarget = window.location.origin || 'https://suigar.com';
		const shareUrl = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareTarget)}`;
		window.open(shareUrl, '_blank', 'noopener,noreferrer');
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
			<DialogContent className="max-w-3xl border border-border/40 bg-background/95 p-0 shadow-2xl">
				<img
					src={suigarSlushHeader}
					alt="Suigar Icon"
					className="absolute left-1/2 top-0 z-10 w-72 -translate-x-1/2 -translate-y-1/2 transform transition-all duration-500 sm:w-80"
				/>
				<div className="flex max-h-[calc(85dvh)] w-full flex-col overflow-y-auto">
					<div className="relative flex flex-col gap-6 p-4 sm:px-8">
						<div className="flex flex-col items-center gap-2 text-center">
							<h2 className="text-3xl font-semibold sm:text-4xl">
								<Badge variant="outline" className="z-1 text-xs font-semibold uppercase tracking-wide shadow-sm">
									{campaignName} · XMAS EDITION
								</Badge>
								<p className="text-sm font-sans font-medium uppercase tracking-wide text-muted-foreground mt-1">{timeframeLabel}</p>
								<span className="block leading-none tracking-tight font-shadow-stroke-3xl font-serif text-5xl sm:text-6xl -rotate-2">
									<span className="inline-block hero-scale-loop">$5000 to win!</span>
								</span>
								<div className="mt-3 flex items-center justify-center gap-6 text-base flex-row sm:gap-6">
									<div className="flex flex-col items-center">
										<span className="text-xl font-semibold uppercase tracking-wide text-success text-stroke">5 jackpots</span>
										<span className="text-xs text-muted-foreground">$1 000 each</span>
									</div>
									<div className="flex flex-col items-center leading-tight">
										{/* <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground/80">Results in</span> */}
										<span className="text-3xl font-bold text-success text-stroke">{campaignTimeLeftLabel}</span>
										<span className="text-xs text-muted-foreground">
											{campaignCountdown.isExpired ? 'Finalizing winners now' : campaignTimeLeftSubtext}
										</span>
									</div>
								</div>
								<div className="mt-4 flex justify-center">
									<img src={fiveWinnersImage} alt="Five winners illustration" className="w-full max-w-md" loading="lazy" />
								</div>
								<span className="text-xl font-medium text-foreground sm:text-2xl">Collect AS MUCH tickets!</span>
							</h2>
						</div>

						<div className="grid gap-4">
							{steps.map((step, index) => {
								const Icon = step.completed ? CheckCircle2 : Circle;
								const progressValue = Math.min(Math.max(step.progress ?? 0, 0), 1) * 100;
								const isPrimaryStep = step.key === 'wager';
								const showBadge = 'badge' in step && step.badge;
								const highlight = step.completed
									? 'border-success/50 bg-success/20'
									: isPrimaryStep
										? 'border-primary/50 bg-primary/5'
										: 'border-border/60 bg-card/80';
								const stepVisual =
									step.key === 'wager' ? (
										<img src={firstTicketImage} alt="First ticket illustration" className="size-24 object-contain" />
									) : step.key === 'extra' ? (
										<DotLottieReact src={suifrenPlayAnimation} loop autoplay className="size-24" />
									) : null;

								return (
									<div key={step.key} className={cn('relative rounded-2xl border p-4 shadow-sm transition-colors duration-300', highlight)}>
										<div className="flex items-start gap-3">
											<Icon className={cn('mt-1 h-5 w-5', step.completed ? 'text-success' : 'text-muted-foreground')} />

											<div className="flex-1 flex flex-row gap-4 items-center">
												<div className="flex-1 space-y-2">
													<div className="flex flex-wrap items-center justify-between gap-2">
														<p className="text-sm text-foreground sm:text-base">
															<span className="font-semibold">{`Step ${index + 1}. `}</span>
															<span className="font-serif text-lg">{step.title}</span>
														</p>
														{showBadge ? (
															<Badge
																variant={step.completed ? 'success' : 'secondary'}
																className="px-2 py-0 text-[11px] font-semibold uppercase tracking-wide"
															>
																{step.badge}
															</Badge>
														) : null}
														{step.key === 'nft' && isAnyBonusLoading ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
													</div>
													<p className="text-xs text-muted-foreground sm:text-sm">{step.description}</p>
													{step.key === 'wager' ? (
														<div className="space-y-2">
															<div className="flex items-center justify-between text-[11px] font-medium text-muted-foreground">
																<span>Progress</span>
																<span>
																	{ticketsLoading
																		? 'Loading…'
																		: `${NUMBER_FORMATTER.format(Math.min(totalWagered, ticketing.firstTicketThreshold))} / ${ticketing.firstTicketThreshold} ${ticketing.wageringCoinSymbol}`}
																</span>
															</div>
															<Progress value={progressValue} indicatorClassName={cn(step.completed ? 'bg-success' : 'bg-primary', 'duration-500')} />
														</div>
													) : null}
													{step.key === 'extra' ? (
														<div className="space-y-2 rounded-xl border-2 border-dashed bg-primary/5 px-3 py-2">
															<div className="flex flex-wrap items-end justify-between gap-4">
																<div className="flex items-baseline gap-2">
																	<span className="text-3xl font-serif leading-none">{ticketStackLabel}</span>
																	<span className="text-xs font-semibold uppercase">{ticketPluralLabel}</span>
																</div>
																<div className="text-right">
																	<span className={cn('text-lg font-serif', additionalTickets > 0 ? 'text-success' : 'text-muted-foreground')}>
																		{additionalTickets > 0 ? `+${INTEGER_FORMATTER.format(additionalTickets)}` : '—'}
																	</span>
																</div>
															</div>
															{showNextTicketMeter ? (
																<>
																	<div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
																		<span>Next ticket in</span>
																		<span className="font-serif text-lg text-foreground">
																			{formattedNextTicketCountdown} {ticketing.wageringCoinSymbol}
																		</span>
																	</div>
																	<Progress value={nextTicketProgress} indicatorClassName="bg-primary" />
																</>
															) : null}
															<p className="text-[11px] font-medium text-muted-foreground/80">{frenzySubtext}</p>
														</div>
													) : null}
													{step.extra ?? null}
												</div>
												{stepVisual ? <div className="shrink-0">{stepVisual}</div> : null}
											</div>
										</div>
									</div>
								);
							})}
						</div>

						<div className="rounded-2xl border border-border/60 bg-card/90 p-4 shadow-sm mb-6">
							<div className="flex flex-row items-center justify-between gap-4">
								<div>
									<p className="text-xs font-semibold uppercase tracking-wide text">Total tickets</p>
									<div className="flex items-baseline gap-2">
										<span className="text-3xl font-serif">{INTEGER_FORMATTER.format(finalTickets)}</span>
										<span className="text-xs text-muted-foreground">{formulaText}</span>
									</div>
								</div>

								<div className="ml-auto flex flex-col gap-1 text-right">
									<Button variant="secondary" size="sm" className="flex items-center justify-center gap-2" onClick={handleShareOnX}>
										<Share2 className="h-4 w-4" />
										Share on X
									</Button>
									<p className="text-[11px] font-medium text-muted-foreground/75">Thanks for spreading the word!</p>
								</div>
							</div>
						</div>
					</div>
					{/* <p className="text-center text-[11px] font-medium text-muted-foreground/70">*25 winners get $100, 125 winners get $20</p> */}
					<DialogFooter className="justify-center border-t border-border/40 bg-background/95 p-4">
						<Button variant={isLogged ? 'primary' : 'secondary'} className="w-full py-3 text-2xl" onClick={handleActionClick}>
							<span className="font-serif font-shadow-stroke-2xl">{isLogged ? 'Play & earn tickets!' : 'Connect wallet to start'}</span>
						</Button>
					</DialogFooter>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default WelcomeModal;
